import { CommonModule } from '@angular/common';
import { Component, Input, inject, output } from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../csr-web-api.service';
import { CareerChallengeMediaUploadResponse } from '../../models';
import { onChallengeImageError, resolveChallengeMediaSrc } from './challenge-media.util';

@Component({
  selector: 'app-challenge-cover-field',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressBarModule
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  templateUrl: './challenge-cover-field.component.html',
  styleUrl: './challenge-cover-field.component.scss'
})
export class ChallengeCoverFieldComponent {
  private readonly api = inject(CsrWebApiService);
  private readonly controlContainer = inject(ControlContainer, { optional: true });

  @Input({ required: true }) challengeId!: string;
  @Input() displayUrl: string | null | nullish = null;
  @Input() canGenerate = false;
  readonly generated = output<CareerChallengeMediaUploadResponse>();

  previewUrl: string | null = null;
  uploading = false;
  generating = false;
  actionError = '';

  get form(): FormGroup {
    return this.controlContainer?.control as FormGroup;
  }

  get storedValue(): string {
    return (this.form?.get('coverImageUrl')?.value as string | null | undefined) ?? '';
  }

  get imageSrc(): string {
    return resolveChallengeMediaSrc(this.previewUrl ?? this.displayUrl, this.storedValue, 'cover');
  }

  onImageError(event: Event): void {
    onChallengeImageError(event, 'cover');
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.form || !this.challengeId) {
      return;
    }

    this.uploading = true;
    this.actionError = '';
    try {
      const response = await firstValueFrom(this.api.uploadCareerChallengeMedia(this.challengeId, file));
      this.form.get('coverImageUrl')?.setValue(response.key);
      this.previewUrl = response.url;
    } catch {
      this.actionError = 'Cover upload failed.';
    } finally {
      this.uploading = false;
    }
  }

  async generateCover(): Promise<void> {
    if (!this.challengeId || !this.canGenerate) {
      return;
    }

    this.generating = true;
    this.actionError = '';
    try {
      const response = await firstValueFrom(this.api.generateCareerChallengeCover(this.challengeId));
      this.form?.get('coverImageUrl')?.setValue(response.key);
      this.previewUrl = response.url;
      this.generated.emit(response);
    } catch {
      this.actionError = 'Cover generation failed. Check title and summary, then try again.';
    } finally {
      this.generating = false;
    }
  }
}

type nullish = null | undefined;